import { AccessGuids, ApplicationNames, ConfigSystemParams, dropdownSelectAllValues } from '../../consts';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AuthenticationService } from '../../services/auth.service';
import { catchError, Observable, throwError } from 'rxjs';
import { CompaniesService } from '../../services/utelize/system/company/companies-service';
import { CompanySelectEvent } from '../../interfaces/events/event-select-company';
import { ComponentAccessUserService } from '../../services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemService } from '../../services/utelize/system/config-system/config-system-service';
import { DropdownService } from '../../services/utelize/general/dropdown-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { IdAndDescriptionDTO } from '../../interfaces/general/id-description';
import { SystemUserDTO } from '../../models/system/user/system-user.dto';
import { TopBarService } from '../../services/utelize/general/top-bar-service';

@Component({
  selector: 'app-dropdown-company',
  templateUrl: './dropdown-company.component.html',
  styleUrls: ['./dropdown-company.component.scss']
})

export class DropdownCompanyComponent implements AfterViewInit, OnChanges, OnInit {

  @Input() disabled               : boolean | undefined;
  @Input() hasFeature             : string  | undefined;
  @Input() hideLabel              : boolean = false;
  @Input() hierarchyFormatting    : boolean | undefined;
  @Input() includeCeased          : boolean = false;
  @Input() initialCompanyId       : number  | undefined;
  @Input() labelTop               : boolean = true;  //CRN_20260529 - false=left of field
  @Input() retainFirstBlankOption : boolean | undefined;
  @Input() showAllSureSimCompanies: boolean | undefined;
  @Input() showSelectAllOption    : boolean = true;
  
  @Output() selectOnChangeEvent : EventEmitter<number            >=new EventEmitter<number            >();
  @Output() selectOnChangeEvent2: EventEmitter<CompanySelectEvent>=new EventEmitter<CompanySelectEvent>();

  get companyHierarchyExpand() { return this.componentAccessUserService.hasAccessGuid(AccessGuids.DropdownsCompanyHierarchyExpand,    true); }
  get hierarchyMode         () { return this.hierarchyFormatting
                                        && !this.componentAccessUserService.hasAccessGuid(AccessGuids.DropdownsCompanyHierarchyGuard, true); }

  get utelizeResellerReplacement() { return this.configSystemService.getValue(ConfigSystemParams.DropdownCompanyUtelizeResellerReplacement, ApplicationNames.eSimPlatform); }

  get selectAllOption() { return this.showSelectAllOption && this.configSystemService.getValueAsBoolean(ConfigSystemParams.DropdownsProvideSelectAllOption, false); }

  private collapsedResellers: Set<number>=new Set<number>();
  private companyIdData!    : IdAndDescriptionDTO[];
  private curUser           : SystemUserDTO;
  private selectedIsReseller: boolean=false;

  public addOrEditForm!    : UntypedFormGroup;
  public firstCompanyOption: string="";
  public viewData          : IdAndDescriptionDTO[]=[];  

  constructor(
    private authenticationService     : AuthenticationService,
    private companiesService          : CompaniesService,
    private componentAccessUserService: ComponentAccessUserService,
    private configSystemService       : ConfigSystemService,
    private dropdownService           : DropdownService,
    private exceptionsService         : ExceptionsService,
    private formBuilder               : FormBuilder,
    private topBarService             : TopBarService
  ) {
    this.curUser=this.authenticationService.currentUserObject();
  }

  ngOnInit(): void {
    try {
      this.getDropdownData();

      let companyId          =this.initialCompanyId==undefined ? 0 : this.initialCompanyId;
      this.firstCompanyOption=!this.hierarchyMode && !this.retainFirstBlankOption && this.selectAllOption ? dropdownSelectAllValues.Company : "";

      this.addOrEditForm=this.formBuilder.group({
        companyID: [companyId, []]
      });

      this.applyDisabledState();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['disabled'])
        this.applyDisabledState();

      if (changes['includeCeased'] && !changes['includeCeased'].firstChange)
        this.getDropdownData(false);

      if (!changes['initialCompanyId'])
        return;

      this.syncSelectedCompanyFromInput();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  ngAfterViewInit(){
    try {
      //CRN_20251119 - moved to getDropdownData(), to ensure company values are available to emit
      //this.selectOnChange(undefined);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getDropdownDataObjectArray() {
    try {
      return this.viewData;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return (undefined);
    }
  }

  private applyDisabledState(): void {
    try {
      const control=this.addOrEditForm?.get('companyID');

      if (control==undefined)
        return;

      if (this.disabled && control.enabled)
        control.disable({ emitEvent: false });
      
      else if (!this.disabled && control.disabled)
        control.enable({ emitEvent: false });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private getDropdownData(emitSelection: boolean=true) {
    try {
      let dataSource: Observable<IdAndDescriptionDTO[]>=this.hierarchyMode
                                                        ? this.companiesService.getSuperHierarchyIdAndDescriptionForUser(this.curUser.userId, this.hasFeature, this.includeCeased)
                                                        : this.companiesService.getHierarchyIdAndDescriptionForUser(this.curUser.userId, this.hasFeature, this.includeCeased);

      dataSource
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          this.companyIdData=(data as IdAndDescriptionDTO[]);

          if (this.utelizeResellerReplacement.trim() != "") {
            this.companyIdData.forEach(item => {
              if (item.description==='Utelize (Reseller)') {
                item.description=this.utelizeResellerReplacement;
              }
            });
          }
          
          //CRN_20251105 - Collapse all Reseller nodes
          if (!this.dropdownService.trackingCompanyIdIsOn() && !this.companyHierarchyExpand) {
            this.collapsedResellers.clear();
            
            for (const item of this.companyIdData) {
              if (this.isResellerRow(item)) {
                this.collapsedResellers.add(<number>item.id);
              }
            }

            //CRN_20251219 - If initial company selected, expand parent reseller to display it
            const initCompanyId=this.initialCompanyId==undefined ? 0 : this.initialCompanyId;
            
            if (initCompanyId>0)
              this.expandToCompany(initCompanyId);
          }

          this.rebuildViewData();

          if (!emitSelection && this.selectedCompanyStillListed())
            return;

          if (!emitSelection)
            this.addOrEditForm?.controls['companyID']?.setValue(0, { emitEvent: false });

          this.selectOnChange(undefined);
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private rebuildViewData(): void {
    try {
      if (!Array.isArray(this.companyIdData)) {
        this.viewData=[];
        return;
      }
  
      const out               : IdAndDescriptionDTO[]=[];
      let   skipChildrenIndent: number               =-1;
  
      for (const row of this.companyIdData) {
        const indent=this.getIndent(row.description ?? '');
  
        if (skipChildrenIndent>=0) {
          if (indent>skipChildrenIndent)
            continue;
          
          skipChildrenIndent=-1;
        }
  
        out.push(row);
  
        if (this.isResellerRow(row) && this.collapsedResellers.has(<number>row.id)) {
          skipChildrenIndent=indent;
        }
      }
  
      this.viewData=out;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public selectOnChange(event: Event | undefined) {
    try {
      if (!this.addOrEditForm || !this.addOrEditForm.controls["companyID"])
        return;
      
      const ctrl        : any    =this.addOrEditForm.controls["companyID"];
      let   companyID   : any    =Number(ctrl.value);
      let   gotCompanyID: boolean=true;
      
      if (companyID===null || companyID===undefined || companyID==="" || isNaN(companyID)) {
        companyID   =-1;
        gotCompanyID=false;
      }
      
      this.topBarService.setTopBarServiceInviewCompanyId(gotCompanyID ? Math.abs(companyID) : this.curUser.companyId);
      
      this.selectOnChangeEvent.emit(companyID);
      
      if (gotCompanyID) {
        this.selectedIsReseller=false;
        
        if (Array.isArray(this.companyIdData)) {
          const selectedItem: IdAndDescriptionDTO | undefined=this.companyIdData.find(item => Number(item.id)===Number(companyID));
          
          if (selectedItem) {
            this.selectedIsReseller=this.isResellerRow(selectedItem) || selectedItem.description==this.utelizeResellerReplacement;
            
            this.selectOnChangeEvent2.emit({ companyID: companyID, isReseller: this.selectedIsReseller});
          }
        }

        if (Array.isArray(this.companyIdData)) {
          if (!this.dropdownService.trackingCompanyIdIsOn() && !this.companyHierarchyExpand) {
            this.collapsedResellers.clear();
            
            for (const item of this.companyIdData) {
              if (this.isResellerRow(item))
                this.collapsedResellers.add(<number>item.id);
            }

            //CRN_20251219 - Keep selected company visible else dropdown is blank
            this.expandToCompany(companyID);
          }
        }
        
        this.rebuildViewData();
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return (undefined);
    }
  }

  private selectedCompanyStillListed(): boolean {
    try {
      const companyID: number=Number(this.addOrEditForm?.controls['companyID']?.value);

      if (!Number.isFinite(companyID) || companyID==0)
        return true;

      return (this.companyIdData ?? []).some(item => Math.abs(Number(item.id))===Math.abs(companyID));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return true;
    }
  }

  private syncSelectedCompanyFromInput(): void {
    try {
      const nextCompanyId: number=this.initialCompanyId==undefined ? 0 : Number(this.initialCompanyId);

      if (!this.addOrEditForm || !this.addOrEditForm.controls['companyID'])
        return;

      const currentCompanyId: number=Number(this.addOrEditForm.controls['companyID'].value);

      if (Number.isFinite(currentCompanyId) && currentCompanyId===nextCompanyId)
        return;

      this.addOrEditForm.controls['companyID'].setValue(nextCompanyId, { emitEvent: false });
      this.selectOnChange(undefined);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public cleanName(s?: string): string {
    try {
      return (s ?? '').replace(/[├└─\u00A0]/g, '').trim();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return s ?? '';
    }
  }
  
  public isResellerRow(item: IdAndDescriptionDTO): boolean {
    try {
      const name=this.cleanName(item?.description ?? "");
      return /\(Reseller\)\s*$/.test(name);
    }
    catch {
      return false;
    }
  }

  private getIndent(desc: string): number {
    try {
      const match=(desc ?? "").match(/^[\s\u00A0│├└─]+/);
      const lead =match ? match[0] : "";
      const level=lead.replace(/[│]/g,"").length;
      
      return level;
    }
    catch {
      return 0;
    }
  }

  public toggleReseller(item: IdAndDescriptionDTO, event: MouseEvent): void {
    try {
      event.stopPropagation();

      if (!this.isResellerRow(item))
        return;
  
      if (this.collapsedResellers.has(<number>item.id))
        this.collapsedResellers.delete(<number>item.id);
      else
        this.collapsedResellers.add(<number>item.id);
  
      this.rebuildViewData();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public isCollapsed(item: IdAndDescriptionDTO): boolean {
    try {
      return this.collapsedResellers.has(<number>item.id);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }
  
  public onSearch(event: any): void {
    try {
      const termRaw   =typeof event==="string" ? event : event?.term;
      const searchTerm=(termRaw ?? "").toLowerCase().trim();
      
      if (!Array.isArray(this.companyIdData)) {
        this.viewData=[];
        return;
      }
      
      if (searchTerm.length===0) {
        this.rebuildViewData();
        return;
      }
      
      this.viewData=this.companyIdData.filter(row => {
        const name=this.cleanName(row.description ?? "").toLowerCase();
        
        return name.includes(searchTerm);
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private expandToCompany(companyID: number): void {
    try {
      if (!Array.isArray(this.companyIdData))
        return;

      const idx=this.companyIdData.findIndex(x => Number(x.id)===Number(companyID));
      
      if (idx<0)
        return;

      let childIndent=this.getIndent(this.companyIdData[idx].description ?? "");

      for (let i=idx-1; i>=0; i--) {
        const rowIndent=this.getIndent(this.companyIdData[i].description ?? "");

        if (rowIndent<childIndent) {
          if (this.isResellerRow(this.companyIdData[i])) {
            this.collapsedResellers.delete(<number>this.companyIdData[i].id);
          }

          childIndent=rowIndent;

          if (childIndent<=0)
            break;
        }
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
